import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  type ElementFormatType,
  type ElementNode,
  type LexicalEditor,
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $createLinkNode, $isLinkNode, $toggleLink } from "@lexical/link";
import { $findMatchingParent } from "@lexical/utils";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from "@lexical/rich-text";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $createCodeNode } from "@lexical/code";
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconBlockquote,
  IconBold,
  IconCode,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconStrikethrough,
  IconUnderline,
} from "@tabler/icons-react";
import IconButton from "../UI/IconButton";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import VariablePicker from "./VariablePicker";
import { VariableContext } from "./VariableContext";
import styles from "./Toolbar.module.css";

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type BlockOption = "paragraph" | HeadingTag;

const HEADINGS: ReadonlyArray<{ value: BlockOption; label: string }> = [
  { value: "paragraph", label: "Normal" },
  { value: "h1", label: "Titre 1" },
  { value: "h2", label: "Titre 2" },
  { value: "h3", label: "Titre 3" },
  { value: "h4", label: "Titre 4" },
  { value: "h5", label: "Titre 5" },
  { value: "h6", label: "Titre 6" },
];

interface FormatState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
}

function applyBlockType(editor: LexicalEditor, type: BlockOption): void {
  editor.update(() => {
    const sel = $getSelection();
    if (!$isRangeSelection(sel)) return;
    if (type === "paragraph") {
      $setBlocksType(sel, () => $createParagraphNode());
    } else {
      $setBlocksType(sel, () => $createHeadingNode(type));
    }
  });
}

export default function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const { available } = useContext(VariableContext);
  const [format, setFormat] = useState<FormatState>({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  });
  const [blockType, setBlockType] = useState<string>("paragraph");
  const [align, setAlign] = useState<ElementFormatType>("left");
  const [currentLink, setCurrentLink] = useState<string | null>(null);
  const [linkDraft, setLinkDraft] = useState<string | null>(null);

  const update = useCallback(() => {
    const sel = $getSelection();
    if (!$isRangeSelection(sel)) return;

    setFormat({
      bold: sel.hasFormat("bold"),
      italic: sel.hasFormat("italic"),
      underline: sel.hasFormat("underline"),
      strikethrough: sel.hasFormat("strikethrough"),
    });

    const anchor = sel.anchor.getNode();
    const linkParent = $findMatchingParent(anchor, $isLinkNode);
    setCurrentLink(linkParent ? linkParent.getURL() : null);

    const el =
      anchor.getKey() === "root" ? anchor : anchor.getTopLevelElementOrThrow();

    if ($isHeadingNode(el)) {
      setBlockType(el.getTag());
    } else if ($isListNode(el)) {
      setBlockType(el.getListType());
    } else {
      setBlockType(el.getType());
    }

    if ($isElementNode(el)) {
      const formatType = el.getFormatType();
      setAlign((formatType || "left") as ElementFormatType);
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        update();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, update]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) =>
      editorState.read(update),
    );
  }, [editor, update]);

  function toggleBlock(type: string, factory: () => ElementNode): void {
    editor.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      $setBlocksType(sel, () =>
        blockType === type ? $createParagraphNode() : factory(),
      );
    });
  }

  function applyLink(): void {
    const url = linkDraft?.trim() ?? "";
    editor.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      const attrs = { target: "_blank", rel: "noopener noreferrer" };
      if (
        url &&
        sel.isCollapsed() &&
        !$findMatchingParent(sel.anchor.getNode(), $isLinkNode)
      ) {
        const linkNode = $createLinkNode(url, attrs);
        linkNode.append($createTextNode(url));
        sel.insertNodes([linkNode]);
      } else {
        $toggleLink(url || null, attrs);
      }
    });
    setLinkDraft(null);
  }

  const headingValue: BlockOption = HEADINGS.some((h) => h.value === blockType)
    ? (blockType as BlockOption)
    : "paragraph";

  return (
    <div className={styles.toolbar} role="toolbar" aria-label="Mise en forme">
      <ReactSelectStandalone
        aria-label="Type de bloc"
        className={styles.select}
        value={HEADINGS.find((h) => h.value === headingValue)}
        options={HEADINGS}
        onChange={(opt: { value: BlockOption } | null) =>
          opt && applyBlockType(editor, opt.value)
        }
        isSearchable={false}
      />

      <div className={styles.separator} />

      <IconButton
        variant="plain"
        icon={IconBold}
        label="Gras"
        active={format.bold}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
      />
      <IconButton
        variant="plain"
        icon={IconItalic}
        label="Italique"
        active={format.italic}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
      />
      <IconButton
        variant="plain"
        icon={IconUnderline}
        label="Souligné"
        active={format.underline}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
      />
      <IconButton
        variant="plain"
        icon={IconStrikethrough}
        label="Barré"
        active={format.strikethrough}
        onClick={() =>
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
        }
      />

      <div className={styles.separator} />

      <IconButton
        variant="plain"
        icon={IconBlockquote}
        label="Citation"
        active={blockType === "quote"}
        onClick={() => toggleBlock("quote", $createQuoteNode)}
      />
      <IconButton
        variant="plain"
        icon={IconCode}
        label="Code"
        active={blockType === "code"}
        onClick={() => toggleBlock("code", $createCodeNode)}
      />

      <div className={styles.separator} />

      <IconButton
        variant="plain"
        icon={IconList}
        label="Liste à puces"
        active={blockType === "bullet"}
        onClick={() =>
          blockType === "bullet"
            ? editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
            : editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
      />
      <IconButton
        variant="plain"
        icon={IconListNumbers}
        label="Liste numérotée"
        active={blockType === "number"}
        onClick={() =>
          blockType === "number"
            ? editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
            : editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
      />

      <div className={styles.separator} />

      <IconButton
        variant="plain"
        icon={IconLink}
        label="Lien"
        active={currentLink !== null || linkDraft !== null}
        onClick={() =>
          setLinkDraft(linkDraft === null ? currentLink ?? "https://" : null)
        }
      />
      {linkDraft !== null && (
        <>
          <input
            className={styles.linkInput}
            type="text"
            aria-label="URL du lien"
            placeholder="https://…"
            value={linkDraft}
            autoFocus
            onChange={(e) => setLinkDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              } else if (e.key === "Escape") {
                setLinkDraft(null);
              }
            }}
          />
          <VariablePicker
            available={available}
            onInsert={(key) => setLinkDraft(`{${key}}`)}
          />
        </>
      )}

      <div className={styles.separator} />

      <IconButton
        variant="plain"
        icon={IconAlignLeft}
        label="Aligner à gauche"
        active={align === "left" || align === ""}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left")}
      />
      <IconButton
        variant="plain"
        icon={IconAlignCenter}
        label="Centrer"
        active={align === "center"}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")}
      />
      <IconButton
        variant="plain"
        icon={IconAlignRight}
        label="Aligner à droite"
        active={align === "right"}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right")}
      />
      <IconButton
        variant="plain"
        icon={IconAlignJustified}
        label="Justifier"
        active={align === "justify"}
        onClick={() =>
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify")
        }
      />
    </div>
  );
}
